import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { firstValueFrom } from 'rxjs';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';
import {
  SITE_PUBLIC_BLOCK_TYPES,
  SITE_PUBLIC_MAX_IMAGE_BYTES,
  SITE_PUBLIC_SECTION_OPTIONS,
  SitePublicBlock,
  SitePublicBlockType,
  SitePublicDetail,
  SitePublicListItem,
  SitePublicSiteOption,
  blockPreviewSrc,
  coverPreviewSrc,
  createEmptySitePublicBlock,
  createEmptySitePublicDetail,
  mapSitePublicDetail,
  mapSitePublicListItem,
  mapSitePublicSiteOption,
  toSitePublicSavePayload,
  SITE_PUBLIC_AMENITIES,
  SITE_PUBLIC_EV_AMENITY,
  SITE_PUBLIC_IMAGE_CATEGORIES,
  SITE_PUBLIC_MAX_GALLERY_IMAGES,
  SitePublicAmenitySelection,
  SitePublicImage,
  SitePublicImageCategory,
  createSitePublicImage,
  imageCategoryLabel,
  imagePreviewSrc,
} from '../../../../core/models/system-settings/site-public-profile.model';

type SitePublicEditTab = 'general' | 'gallery' | 'amenity' | 'content';

@Component({
  selector: 'app-site-public-profiles',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalAdminActivityHistoryComponent,
  ],
  templateUrl: './site-public-profiles.component.html',
  styleUrl: './site-public-profiles.component.css',
})
export class SitePublicProfilesComponent implements OnInit {
  private readonly api = inject(ApiSystemSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  readonly blockTypeOptions = SITE_PUBLIC_BLOCK_TYPES;
  readonly sectionOptions = SITE_PUBLIC_SECTION_OPTIONS;
  readonly activityEntityTypes = AdminActivityEntityTypes;
  readonly maxImageBytes = SITE_PUBLIC_MAX_IMAGE_BYTES;
  readonly pageSizeOptions = [10, 20, 50, 100];

  items = signal<SitePublicListItem[]>([]);
  totalCount = signal(0);
  page = signal(1);
  pageSize = signal(20);
  isLoading = signal(false);
  isSaving = signal(false);
  lastFetchedTime = signal<Date | null>(null);
  isDeleteConfirmOpen = signal(false);
  deleteTarget = signal<SitePublicListItem | null>(null);

  searchText = '';
  filterPublished: '' | 'true' | 'false' = '';
  filterActive: '' | 'true' | 'false' = 'true';
  filterSiteOnline: '' | 'true' | 'false' = '';

  viewMode = signal<'list' | 'edit'>('list');
  draft = signal<SitePublicDetail>(createEmptySitePublicDetail());
  showActivityHistory = false;
  showImageModal = false;
  selectedImageUrl = '';

  siteOptions = signal<SitePublicSiteOption[]>([]);
  siteSearchText = '';
  showSiteDropdown = false;

  trackById = (_: number, item: SitePublicListItem) => item.id;
  trackByBlock = (_: number, b: SitePublicBlock) => b.uiKey;

  ngOnInit(): void {
    this.reload();
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize()));
  }

  get startItemIndex(): number {
    if (this.totalCount() === 0) return 0;
    return (this.page() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.page() * this.pageSize(), this.totalCount());
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const current = this.page();
    const windowSize = 5;
    let start = Math.max(1, current - Math.floor(windowSize / 2));
    const end = Math.min(total, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  get canShowActivityHistory(): boolean {
    return (this.draft()?.id ?? 0) > 0;
  }

  get filteredSiteOptions(): SitePublicSiteOption[] {
    const q = this.siteSearchText.trim().toLowerCase();
    const selectedId = this.draft().siteId;
    return this.siteOptions().filter((s) => {
      if (s.hasProfile && s.siteId !== selectedId) return false;
      if (!q) return true;
      return s.name.toLowerCase().includes(q) || String(s.siteId).includes(q);
    });
  }

  coverSrc(detail: SitePublicDetail): string {
    return coverPreviewSrc(detail);
  }

  blockSrc(block: SitePublicBlock): string {
    return blockPreviewSrc(block);
  }

  reload(): void {
    this.isLoading.set(true);
    this.api
      .getSitePublicProfilesForTable({
        search: this.searchText,
        isPublished:
          this.filterPublished === '' ? null : this.filterPublished === 'true',
        isActive: this.filterActive === '' ? null : this.filterActive === 'true',
        siteIsOnline:
          this.filterSiteOnline === '' ? null : this.filterSiteOnline === 'true',
        page: this.page(),
        pageSize: this.pageSize(),
      })
      .subscribe({
        next: (res) => {
          const rows = res?.items ?? res?.Items ?? [];
          this.items.set(rows.map(mapSitePublicListItem));
          this.totalCount.set(Number(res?.total ?? res?.Total ?? 0));
          this.lastFetchedTime.set(new Date());
          this.isLoading.set(false);
        },
        error: () => {
          this.toast.error('โหลดรายการลานจอดล้มเหลว');
          this.isLoading.set(false);
        },
      });
  }

  onSearch(): void {
    this.page.set(1);
    this.reload();
  }

  onPageChange(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page.set(next);
    this.reload();
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!Number.isFinite(value) || value <= 0) return;
    this.pageSize.set(value);
    this.page.set(1);
    this.reload();
  }

  openCreate(): void {
    if (!this.canCreate) return;
    this.draft.set(createEmptySitePublicDetail());
    this.siteSearchText = '';
    this.showSiteDropdown = false;
    this.editTab.set('general');
    this.viewMode.set('edit');
    void this.loadSiteOptions();
  }

  openEdit(item: SitePublicListItem): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.isLoading.set(true);
    this.api.getSitePublicProfileById(item.id).subscribe({
      next: (res) => {
        const detail = mapSitePublicDetail(res);
        this.draft.set(detail);
        this.siteSearchText = detail.siteName || '';
        this.showSiteDropdown = false;
        this.editTab.set('general');
        this.viewMode.set('edit');
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('โหลดรายละเอียดลานจอดล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  backToList(): void {
    this.viewMode.set('list');
    this.draft.set(createEmptySitePublicDetail());
    this.siteSearchText = '';
    this.showSiteDropdown = false;
  }

  patchDraft(partial: Partial<SitePublicDetail>): void {
    this.draft.update((d) => ({ ...d, ...partial }));
  }

  openSiteDropdown(): void {
    if (this.draft().id > 0) return;
    this.showSiteDropdown = true;
    if (!this.siteOptions().length) {
      void this.loadSiteOptions(this.siteSearchText);
    }
  }

  closeSiteDropdown(): void {
    this.showSiteDropdown = false;
    const d = this.draft();
    if (d.siteId > 0) {
      this.siteSearchText = d.siteName || this.siteSearchText;
    } else if (this.siteSearchText.trim()) {
      const match = this.filteredSiteOptions.find(
        (s) => s.name.toLowerCase() === this.siteSearchText.trim().toLowerCase(),
      );
      if (match) {
        this.selectSite(match);
      } else {
        this.siteSearchText = '';
      }
    }
  }

  onSiteSearchInput(value: string): void {
    this.siteSearchText = value;
    this.showSiteDropdown = true;
    if (this.draft().siteId > 0) {
      this.patchDraft({ siteId: 0, siteName: '', siteIsOnline: false });
    }
  }

  selectSite(option: SitePublicSiteOption): void {
    if (this.draft().id > 0) return;
    this.patchDraft({
      siteId: option.siteId,
      siteName: option.name,
      siteIsOnline: option.isOnline,
      siteHasEv: option.hasEv,
      amenities: option.defaultAmenityCodes.map((code, i) => ({
        code,
        noteTh: '',
        noteEn: '',
        sortOrder: (i + 1) * 10,
      })),
    });
    this.siteSearchText = option.name;
    this.showSiteDropdown = false;
  }

  addBlock(type: SitePublicBlockType = 'text'): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.draft.update((d) => {
      const nextSort = d.blocks.length
        ? Math.max(...d.blocks.map((b) => b.sortOrder)) + 10
        : 10;
      const block = createEmptySitePublicBlock(nextSort);
      block.blockType = type;
      return { ...d, blocks: [...d.blocks, block] };
    });
  }

  removeBlock(index: number): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.draft.update((d) => ({
      ...d,
      blocks: d.blocks.filter((_, i) => i !== index),
    }));
  }

  moveBlock(index: number, direction: -1 | 1): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.draft.update((d) => {
      const blocks = [...d.blocks];
      const target = index + direction;
      if (target < 0 || target >= blocks.length) return d;
      [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
      blocks.forEach((b, i) => (b.sortOrder = (i + 1) * 10));
      return { ...d, blocks };
    });
  }

  /** mutate แถวเดิมเพื่อไม่ทำลาย focus ตอนพิมพ์ */
  patchBlock(index: number, partial: Partial<SitePublicBlock>): void {
    this.draft.update((d) => {
      const block = d.blocks[index];
      if (block) Object.assign(block, partial);
      return { ...d, blocks: d.blocks };
    });
  }

  onCoverSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.canEditDraft()) {
      input.value = '';
      return;
    }
    this.readImageFile(file, (base64, name) => {
      this.patchDraft({
        coverPendingBase64: base64,
        coverPendingFileName: name,
      });
      this.toast.success('แนบรูปปกแล้ว — กดบันทึกเพื่ออัปโหลด');
    });
    input.value = '';
  }

  clearCover(): void {
    this.patchDraft({
      coverPendingBase64: null,
      coverPendingFileName: null,
      coverImage: '',
      coverImagePath: '',
      coverImageExt: '',
    });
  }

  onBlockImageSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.canEditDraft()) {
      input.value = '';
      return;
    }
    this.readImageFile(file, (base64, name) => {
      this.patchBlock(index, {
        blockType: 'image',
        pendingBase64: base64,
        pendingFileName: name,
      });
      this.toast.success('แนบรูปแล้ว — กดบันทึกเพื่ออัปโหลด');
    });
    input.value = '';
  }

  clearBlockImage(index: number): void {
    this.patchBlock(index, {
      pendingBase64: null,
      pendingFileName: null,
      image: '',
      imagePath: '',
      imageExt: '',
    });
  }

  async onSave(): Promise<void> {
    if (!this.canEditDraft()) return;
    const auth = this.resolveAuth();
    if (!auth) return;

    const profile = this.draft();
    if (!profile.siteId || profile.siteId <= 0) {
      this.toast.error('กรุณาเลือกลานจอด');
      return;
    }
    if (!profile.titleTh?.trim()) {
      this.toast.error('กรุณาระบุชื่อที่แสดง (TH)');
      return;
    }
    if (profile.isPublished && !coverPreviewSrc(profile)) {
      this.toast.error('เผยแพร่ต้องมีรูปปก');
      this.editTab.set('general');
      return;
    }
    if (profile.images.length > this.maxGalleryImages) {
      this.toast.error(`รูป gallery ได้ไม่เกิน ${this.maxGalleryImages} รูป`);
      this.editTab.set('gallery');
      return;
    }

    this.isSaving.set(true);
    try {
      const withUploads = await this.uploadPending(profile, auth);
      this.draft.set(withUploads);

      const res = await firstValueFrom(
        this.api.saveSitePublicProfile(
          toSitePublicSavePayload(withUploads, auth.userId, auth.menuId),
        ),
      );
      const ok = res?.isSuccess ?? res?.IsSuccess ?? res?.success ?? true;
      if (ok === false) {
        this.toast.error(res?.message ?? res?.Message ?? 'บันทึกไม่สำเร็จ');
        return;
      }
      this.toast.success(res?.message ?? res?.Message ?? 'บันทึกเรียบร้อย');
      this.viewMode.set('list');
      this.reload();
    } catch {
      this.toast.error('บันทึกลานจอดล้มเหลว');
    } finally {
      this.isSaving.set(false);
    }
  }

  askDelete(item: SitePublicListItem): void {
    if (!this.canDelete) return;
    this.deleteTarget.set(item);
    this.isDeleteConfirmOpen.set(true);
  }

  closeDeleteConfirm(): void {
    this.isDeleteConfirmOpen.set(false);
    this.deleteTarget.set(null);
  }

  async confirmDelete(): Promise<void> {
    const item = this.deleteTarget();
    if (!item) return;
    await this.onDelete(item);
  }

  async onDelete(item: SitePublicListItem): Promise<void> {
    if (!this.canDelete) return;
    const auth = this.resolveAuth();
    if (!auth) return;

    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(
        this.api.deleteSitePublicProfile({
          userId: auth.userId,
          menuId: auth.menuId,
          id: item.id,
        }),
      );
      const ok = res?.isSuccess ?? res?.IsSuccess ?? true;
      if (ok === false) {
        this.toast.error(res?.message ?? res?.Message ?? 'ลบไม่สำเร็จ');
        return;
      }
      this.toast.success(res?.message ?? res?.Message ?? 'ลบเรียบร้อย');
      this.closeDeleteConfirm();
      this.reload();
    } catch {
      this.toast.error('ลบลานจอดล้มเหลว');
    } finally {
      this.isSaving.set(false);
    }
  }

  openImageModal(url: string): void {
    if (!url) return;
    this.selectedImageUrl = url;
    this.showImageModal = true;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImageUrl = '';
  }

  openHistory(): void {
    this.showActivityHistory = true;
  }

  private canEditDraft(): boolean {
    const id = this.draft().id;
    return id > 0 ? this.canUpdate : this.canCreate;
  }

  private async loadSiteOptions(search?: string): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.api.getSitePublicProfileSiteOptions(search?.trim() || undefined),
      );
      const rows = Array.isArray(res) ? res : [];
      this.siteOptions.set(rows.map(mapSitePublicSiteOption));
    } catch {
      this.toast.error('โหลดรายการลานจอดให้เลือกล้มเหลว');
    }
  }

  private async uploadPending(
    profile: SitePublicDetail,
    auth: { userId: number; menuId: number },
  ): Promise<SitePublicDetail> {
    let next = {
      ...profile,
      blocks: profile.blocks.map((b) => ({ ...b })),
      images: profile.images.map((img) => ({ ...img })),
    };

    for (let i = 0; i < next.images.length; i++) {
      const image = next.images[i];
      if (!image.pendingBase64) continue;
      const uploaded = await this.uploadOne(image.pendingBase64, image.pendingFileName, auth);
      next.images[i] = {
        ...image,
        imagePath: uploaded.path,
        imageExt: uploaded.ext,
        image: uploaded.url,
        pendingBase64: null,
        pendingFileName: null,
      };
    }

    if (next.coverPendingBase64) {
      const uploaded = await this.uploadOne(
        next.coverPendingBase64,
        next.coverPendingFileName,
        auth,
      );
      next = {
        ...next,
        coverImagePath: uploaded.path,
        coverImageExt: uploaded.ext,
        coverImage: uploaded.url,
        coverPendingBase64: null,
        coverPendingFileName: null,
      };
    }

    for (let i = 0; i < next.blocks.length; i++) {
      const block = next.blocks[i];
      if (block.blockType !== 'image' || !block.pendingBase64) continue;
      const uploaded = await this.uploadOne(
        block.pendingBase64,
        block.pendingFileName,
        auth,
      );
      next.blocks[i] = {
        ...block,
        imagePath: uploaded.path,
        imageExt: uploaded.ext,
        image: uploaded.url,
        pendingBase64: null,
        pendingFileName: null,
      };
    }

    return next;
  }

  private async uploadOne(
    base64: string,
    fileName: string | null,
    auth: { userId: number; menuId: number },
  ): Promise<{ path: string; ext: string; url: string }> {
    const res = await firstValueFrom(
      this.api.uploadSitePublicImage({
        userId: auth.userId,
        menuId: auth.menuId,
        base64,
        fileName: fileName || undefined,
      }),
    );
    const ok = res?.isSuccess ?? res?.IsSuccess ?? false;
    const path = res?.imagePath ?? res?.ImagePath ?? '';
    const ext = res?.imageExt ?? res?.ImageExt ?? '';
    const url = res?.imageUrl ?? res?.ImageUrl ?? '';
    if (!ok || !path || !ext) {
      throw new Error(res?.message ?? res?.Message ?? 'อัปโหลดรูปไม่สำเร็จ');
    }
    return { path, ext, url };
  }

  private readImageFile(file: File, onOk: (base64: string, name: string) => void): void {
    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    if (file.size > this.maxImageBytes) {
      this.toast.error('ไฟล์ใหญ่เกิน 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onOk(String(reader.result || ''), file.name);
    reader.readAsDataURL(file);
  }

  private resolveAuth(): { userId: number; menuId: number } | null {
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!menuId) {
      this.toast.error(
        'ไม่พบสิทธิ์เมนูนี้ — ตรวจว่า path /system-settings/site-public-profiles (path_id 128) ถูกผูก role แล้ว',
      );
      return null;
    }
    return { userId: 0, menuId };
  }

  /* ---------- Tabs ---------- */
  readonly editTabs: { value: SitePublicEditTab; label: string; icon: string }[] = [
    { value: 'general', label: 'ข้อมูลทั่วไป', icon: 'info' },
    { value: 'gallery', label: 'รูปภาพ', icon: 'photo_library' },
    { value: 'amenity', label: 'สิ่งอำนวยความสะดวก', icon: 'verified_user' },
    { value: 'content', label: 'เนื้อหา / ค่าจอด', icon: 'view_agenda' },
  ];
  editTab = signal<SitePublicEditTab>('general');

  setEditTab(tab: SitePublicEditTab): void {
    this.editTab.set(tab);
  }

  editTabBadge(tab: SitePublicEditTab): number | null {
    const d = this.draft();
    if (tab === 'gallery') return d.images.length;
    if (tab === 'amenity') return this.enabledAmenityCount;
    if (tab === 'content') return d.blocks.length;
    return null;
  }

  /* ---------- Gallery ---------- */
  readonly imageCategories = SITE_PUBLIC_IMAGE_CATEGORIES;
  readonly maxGalleryImages = SITE_PUBLIC_MAX_GALLERY_IMAGES;
  uploadCategory: SitePublicImageCategory = 'parking';
  galleryFilter: SitePublicImageCategory | '' = '';

  trackByImage = (_: number, img: SitePublicImage) => img.uiKey;

  get filteredImages(): SitePublicImage[] {
    const images = this.draft().images;
    return this.galleryFilter ? images.filter((img) => img.category === this.galleryFilter) : images;
  }

  galleryCount(category: SitePublicImageCategory | ''): number {
    const images = this.draft().images;
    return category ? images.filter((img) => img.category === category).length : images.length;
  }

  imageSrc(image: SitePublicImage): string {
    return imagePreviewSrc(image);
  }

  categoryLabel(category: string): string {
    return imageCategoryLabel(category);
  }

  setGalleryFilter(category: SitePublicImageCategory | ''): void {
    this.galleryFilter = category;
    if (category) this.uploadCategory = category;
  }

  onGallerySelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length || !this.canEditDraft()) return;

    const room = this.maxGalleryImages - this.draft().images.length;
    if (room <= 0) {
      this.toast.error(`รูป gallery ได้ไม่เกิน ${this.maxGalleryImages} รูป`);
      return;
    }
    if (files.length > room) {
      this.toast.warn(`เพิ่มได้อีก ${room} รูป — ระบบจะแนบเฉพาะ ${room} รูปแรก`);
    }

    const category = this.uploadCategory;
    files.slice(0, room).forEach((file) => {
      this.readImageFile(file, (base64, name) => {
        this.draft.update((d) => {
          const nextSort = d.images.length ? Math.max(...d.images.map((img) => img.sortOrder)) + 10 : 10;
          return { ...d, images: [...d.images, createSitePublicImage(category, nextSort, base64, name)] };
        });
      });
    });
    this.toast.success('แนบรูปแล้ว — กดบันทึกเพื่ออัปโหลด');
  }

  /** mutate แถวเดิมเพื่อไม่ทำลาย focus ตอนพิมพ์ */
  patchImage(uiKey: string, partial: Partial<SitePublicImage>): void {
    this.draft.update((d) => {
      const image = d.images.find((img) => img.uiKey === uiKey);
      if (image) Object.assign(image, partial);
      return { ...d, images: d.images };
    });
  }

  removeImage(uiKey: string): void {
    if (!this.canEditDraft()) return;
    this.draft.update((d) => ({ ...d, images: d.images.filter((img) => img.uiKey !== uiKey) }));
  }

  moveImage(uiKey: string, direction: -1 | 1): void {
    if (!this.canEditDraft()) return;
    this.draft.update((d) => {
      const images = [...d.images];
      const index = images.findIndex((img) => img.uiKey === uiKey);
      const visible = this.galleryFilter
        ? images.map((img, i) => ({ img, i })).filter((x) => x.img.category === this.galleryFilter).map((x) => x.i)
        : images.map((_, i) => i);
      const pos = visible.indexOf(index);
      const targetPos = pos + direction;
      if (pos < 0 || targetPos < 0 || targetPos >= visible.length) return d;
      const target = visible[targetPos];
      [images[index], images[target]] = [images[target], images[index]];
      images.forEach((img, i) => (img.sortOrder = (i + 1) * 10));
      return { ...d, images };
    });
  }

  /* ---------- Amenities ---------- */
  readonly amenityOptions = SITE_PUBLIC_AMENITIES;
  readonly evAmenityCode = SITE_PUBLIC_EV_AMENITY;

  get enabledAmenityCount(): number {
    const d = this.draft();
    const manual = d.amenities.filter((a) => a.code !== SITE_PUBLIC_EV_AMENITY).length;
    return manual + (d.siteHasEv ? 1 : 0);
  }

  isAmenityOn(code: string): boolean {
    if (code === SITE_PUBLIC_EV_AMENITY) return this.draft().siteHasEv;
    return this.draft().amenities.some((a) => a.code === code);
  }

  amenitySelection(code: string): SitePublicAmenitySelection | undefined {
    return this.draft().amenities.find((a) => a.code === code);
  }

  toggleAmenity(code: string): void {
    if (code === SITE_PUBLIC_EV_AMENITY || !this.canEditDraft()) return;
    this.draft.update((d) => {
      const exists = d.amenities.some((a) => a.code === code);
      const amenities = exists
        ? d.amenities.filter((a) => a.code !== code)
        : [...d.amenities, { code, noteTh: '', noteEn: '', sortOrder: (d.amenities.length + 1) * 10 }];
      return { ...d, amenities };
    });
  }

  patchAmenityNote(code: string, field: 'noteTh' | 'noteEn', value: string): void {
    this.draft.update((d) => {
      const existing = d.amenities.find((a) => a.code === code);
      if (existing) {
        existing[field] = value;
        return { ...d, amenities: d.amenities };
      }
      const created: SitePublicAmenitySelection = { code, noteTh: '', noteEn: '', sortOrder: 0 };
      created[field] = value;
      return { ...d, amenities: [created, ...d.amenities] };
    });
  }
}
